import { type ReactNode } from "react";
import styled from "@emotion/styled";
import { Badge } from "../../feedback/Badge";

export type CommandMenuTone = "neutral" | "primary" | "success" | "warning" | "danger" | "info";

export type CommandMenuItem = {
  /** Stable id for the item. */
  id: string;
  /** Primary label, e.g. a command name. */
  label: ReactNode;
  /** Secondary description line. */
  description?: ReactNode;
  /** Leading icon node, rendered inside a tinted square. */
  icon?: ReactNode;
  /** Extra text used for filtering (in addition to a string `label`). */
  keywords?: string;
  /** Optional trailing status badge. */
  status?: { label: ReactNode; tone?: CommandMenuTone };
  disabled?: boolean;
};

export type CommandMenuProps = {
  /** Items to render. */
  items: CommandMenuItem[];
  /** Current filter query. When set and `filter` is not false, items are filtered. */
  query?: string;
  /** Optional uppercase title shown above the list. */
  title?: ReactNode;
  /** Text shown when no items match. */
  emptyText?: ReactNode;
  /** Whether to internally filter `items` by `query`. Defaults to true. */
  filter?: boolean;
  /** Called when an item is selected. */
  onSelect?: (item: CommandMenuItem) => void;
  className?: string;
};

/* Exact compiled output of the pre-migration Tailwind utilities. */

const CommandMenuRoot = styled("div", {
  label: "AgenticCommandMenu-root"
})({
  overflow: "hidden",
  borderRadius: "var(--bfrs-radius-xl)",
  borderWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
  "--tw-shadow": "var(--bfrs-shadow-menu)",
  "--tw-shadow-colored": "var(--bfrs-shadow-menu)",
  boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)"
});

const CommandMenuTitleBar = styled("div", {
  label: "AgenticCommandMenu-titleBar"
})({
  borderBottomWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border-light) / var(--tw-border-opacity, 1))",
  paddingLeft: ".75rem",
  paddingRight: ".75rem",
  paddingBottom: ".375rem",
  paddingTop: ".5rem"
});

const CommandMenuTitle = styled("span", {
  label: "AgenticCommandMenu-title"
})({
  fontSize: "10px",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: ".07em",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

const CommandMenuEmpty = styled("div", {
  label: "AgenticCommandMenu-empty"
})({
  paddingLeft: ".875rem",
  paddingRight: ".875rem",
  paddingTop: "1rem",
  paddingBottom: "1rem",
  textAlign: "center",
  fontSize: ".75rem",
  lineHeight: "1rem",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

const CommandMenuOption = styled("button", {
  label: "AgenticCommandMenu-option"
})({
  display: "flex",
  width: "100%",
  alignItems: "center",
  gap: ".625rem",
  paddingLeft: ".875rem",
  paddingRight: ".875rem",
  paddingTop: ".625rem",
  paddingBottom: ".625rem",
  textAlign: "left",
  transitionProperty: "color,background-color,border-color,text-decoration-color,fill,stroke",
  transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
  transitionDuration: ".15s",
  "&:hover": {
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-muted) / var(--tw-bg-opacity, 1))"
  },
  "&:disabled": {
    cursor: "not-allowed",
    opacity: ".5"
  }
});

const CommandMenuIconTile = styled("span", {
  label: "AgenticCommandMenu-iconTile"
})({
  display: "flex",
  width: "30px",
  height: "30px",
  flexShrink: 0,
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "var(--bfrs-radius-lg)",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-bg-opacity, 1))",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-primary) / var(--tw-text-opacity, 1))"
});

const CommandMenuOptionText = styled("span", {
  label: "AgenticCommandMenu-optionText"
})({
  minWidth: "0px",
  flex: "1 1 0%"
});

const CommandMenuOptionLabel = styled("span", {
  label: "AgenticCommandMenu-optionLabel"
})({
  display: "block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontSize: "12.5px",
  fontWeight: 500,
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
});

const CommandMenuOptionDescription = styled("span", {
  label: "AgenticCommandMenu-optionDescription"
})({
  display: "block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontSize: "11px",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
});

const CommandMenuBadge = styled(Badge, {
  label: "AgenticCommandMenu-badge"
})({
  "&&": { flexShrink: 0 }
});

function itemHaystack(item: CommandMenuItem): string {
  const labelText = typeof item.label === "string" ? item.label : "";
  return `${labelText} ${item.keywords ?? ""}`.toLowerCase();
}

/**
 * Searchable command / option palette. Renders a floating-style surface with a
 * list of icon + label + description rows and optional status badges. Filtering
 * is internal by default; pass `filter={false}` to control the list yourself.
 *
 * The component is position-agnostic — wrap it (or pass `className`) to place it
 * as a popover above an input, in a dropdown, etc.
 */
export function CommandMenu({
  items,
  query = "",
  title,
  emptyText = "No results found",
  filter = true,
  onSelect,
  className
}: CommandMenuProps) {
  const normalizedQuery = query.trim().toLowerCase();
  const visible = filter && normalizedQuery
    ? items.filter((item) => itemHaystack(item).includes(normalizedQuery))
    : items;

  return (
    <CommandMenuRoot role="listbox" className={className}>
      {title && (
        <CommandMenuTitleBar>
          <CommandMenuTitle>{title}</CommandMenuTitle>
        </CommandMenuTitleBar>
      )}

      {visible.length === 0 ? (
        <CommandMenuEmpty>{emptyText}</CommandMenuEmpty>
      ) : (
        visible.map((item) => (
          <CommandMenuOption
            key={item.id}
            type="button"
            role="option"
            disabled={item.disabled}
            onClick={() => onSelect?.(item)}
          >
            {item.icon && <CommandMenuIconTile>{item.icon}</CommandMenuIconTile>}
            <CommandMenuOptionText>
              <CommandMenuOptionLabel>{item.label}</CommandMenuOptionLabel>
              {item.description && <CommandMenuOptionDescription>{item.description}</CommandMenuOptionDescription>}
            </CommandMenuOptionText>
            {item.status && (
              <CommandMenuBadge size="xs" tone={item.status.tone ?? "neutral"}>
                {item.status.label}
              </CommandMenuBadge>
            )}
          </CommandMenuOption>
        ))
      )}
    </CommandMenuRoot>
  );
}

CommandMenu.displayName = "CommandMenu";
